/**
 * MessageReferences Component
 * Displays context sources (files and reflection nodes) used in AI response generation
 * Inline expansion bar with smooth collapse animation - no truncation, shows all sources
 */

import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Button,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LinkIcon from '@mui/icons-material/Link';
import { useUntypedTranslation } from '../../hooks/useUntypedTranslation';
import { FileSource, InternalNodeSource } from '../../models/Message';

/**
 * Component props
 */
interface MessageReferencesProps {
  /** Files used in response generation */
  filesUsed?: FileSource[];
  /** Internal reflection nodes used in response generation */
  internalNodesUsed?: InternalNodeSource[];
}

/**
 * Styled components
 */
const SectionHeader = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.75rem',
  marginBottom: theme.spacing(0.5),
  marginTop: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  color: theme.palette.text.secondary,
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(0.25, 0),
  paddingLeft: 0,
  paddingRight: 0,
  '&:last-child': {
    paddingBottom: 0,
  },
}));

const ContentContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(0.5),
  paddingLeft: theme.spacing(2),
  paddingBottom: theme.spacing(1),
  borderLeft: `2px solid ${theme.palette.divider}`,
  maxHeight: 300,
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: 4,
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.grey[300],
    borderRadius: 2,
    '&:hover': {
      backgroundColor: theme.palette.grey[400],
    },
  },
}));

/**
 * MessageReferences Component
 */
const MessageReferences: React.FC<MessageReferencesProps> = ({
  filesUsed,
  internalNodesUsed,
}) => {
  const { t } = useUntypedTranslation();
  const [expanded, setExpanded] = useState(false);

  // Calculate total sources
  const filesCount = filesUsed?.length || 0;
  const nodesCount = internalNodesUsed?.length || 0;
  const totalSources = filesCount + nodesCount;

  // Don't render if no sources
  if (totalSources === 0) {
    return null;
  }

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  return (
    <Box>
      {/* Minimal text button - just count with arrow */}
      <Button
        variant="text"
        size="small"
        onClick={handleToggle}
        endIcon={
          expanded ? 
          <KeyboardArrowUpIcon sx={{ fontSize: '1rem' }} /> : 
          <KeyboardArrowDownIcon sx={{ fontSize: '1rem' }} />
        }
        sx={{
          mt: 1,
          px: 0.5,
          py: 0.25,
          minWidth: 'auto',
          textTransform: 'none',
          color: 'text.secondary',
          fontSize: '0.75rem',
          fontWeight: 400,
          '&:hover': {
            backgroundColor: 'transparent',
            color: 'primary.main',
          },
        }}
      >
        <LinkIcon sx={{ fontSize: '0.9rem', mr: 0.5 }} />
        {totalSources === 1 
          ? t('chat.references.sourcesUsed', { count: totalSources })
          : t('chat.references.sourcesUsed_plural', { count: totalSources })
        }
      </Button>

      {/* Expanded Content with minimal left border */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <ContentContainer>
          {/* Files Section */}
          {filesCount > 0 && (
            <Box>
              <SectionHeader>
                <InsertDriveFileIcon sx={{ fontSize: '0.9rem' }} />
                {t('chat.references.filesUsedCount', {
                  count: filesCount,
                  defaultValue: `Files Used (${filesCount})`,
                })}
              </SectionHeader>
              <List dense disablePadding>
                {filesUsed?.map((file) => (
                  <StyledListItem key={file.document_id}>
                    <ListItemText
                      primary={file.file_name}
                      primaryTypographyProps={{
                        variant: 'body2',
                        sx: {
                          fontSize: '0.8rem',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                          color: 'text.secondary',
                        },
                      }}
                    />
                  </StyledListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Reflection Steps Section */}
          {nodesCount > 0 && (
            <Box sx={{ mt: filesCount > 0 ? 1.5 : 0 }}>
              <SectionHeader>
                <AccountTreeIcon sx={{ fontSize: '0.9rem' }} />
                {t('chat.references.reflectionStepsUsedCount', {
                  count: nodesCount,
                  defaultValue: `Reflection Steps Used (${nodesCount})`,
                })}
              </SectionHeader>
              <List dense disablePadding>
                {internalNodesUsed?.map((node) => (
                  <StyledListItem key={node.node_id}>
                    <ListItemText
                      primary={node.node_name}
                      primaryTypographyProps={{
                        variant: 'body2',
                        sx: {
                          fontSize: '0.8rem',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                          color: 'text.secondary',
                        },
                      }}
                    />
                  </StyledListItem>
                ))}
              </List>
            </Box>
          )}
        </ContentContainer>
      </Collapse>
    </Box>
  );
};

export default MessageReferences;

